import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import classesPropType from "@xo-union/classes-prop-type";
import FocusLock from "react-focus-lock";
import noop from "lodash/noop";
import { DialogHeadingDescribeIDsProvider } from "../DialogHeading/index.js";
import { useModalRegisterEffect } from "../ModalRegister/index.js";
import useModalEffects from "../useModalEffects/index.js";

/**
 * A simple wrapper with some basic accessibility features for modals.
 *
 * ### Features
 *
 * 1. Disable scroll on document body
 * 2. Lock focus
 * 3. Keyboard accessibility (Closing on pressing ESC)
 * 4. Outside click handling
 * 5. Label association with nested `DialogHeading` elements
 */
const ModalContainer = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    as: Type,
    classes,
    enabled,
    onClose,
    onClickOutside,
    onEscapeKey,
    initialFocusRef,
    children,
    autoFocus,
    focusLockProps,
    ...props
  } = _ref;
  const dialogRef = React.useRef();
  React.useImperativeHandle(ref, () => dialogRef.current);
  const activateFocusLock = React.useCallback(() => {
    var _initialFocusRef$curr;
    return initialFocusRef === null || initialFocusRef === void 0 ? void 0 : (_initialFocusRef$curr = initialFocusRef.current) === null || _initialFocusRef$curr === void 0 ? void 0 : _initialFocusRef$curr.focus();
  }, [initialFocusRef]);
  useModalEffects({
    onClose,
    onClickOutside,
    onEscapeKey,
    enabled,
    classes
  }, dialogRef);
  useModalRegisterEffect(enabled);
  const ariaProps = enabled ? {
    role: 'dialog',
    'aria-modal': 'true'
  } : {
    role: 'none',
    'aria-hidden': 'true'
  };
  return /*#__PURE__*/React.createElement(FocusLock, _extends({
    disabled: !enabled,
    autoFocus: initialFocusRef ? false : autoFocus,
    onActivation: initialFocusRef && activateFocusLock,
    returnFocus: true
  }, focusLockProps), /*#__PURE__*/React.createElement(DialogHeadingDescribeIDsProvider, null, headerDescribeIDs => /*#__PURE__*/React.createElement(Type, _extends({}, ariaProps, {
    ref: dialogRef,
    "aria-labelledby": headerDescribeIDs
  }, props), children)));
});
ModalContainer.defaultProps = {
  autoFocus: true,
  enabled: true,
  as: 'div',
  onClose: noop,
  onClickOutside: noop,
  onEscapeKey: noop
};
process.env.NODE_ENV !== "production" ? ModalContainer.propTypes = {
  /**
   * Underlying element type for container
   */
  as: PropTypes.elementType,
  /**
   * Children to render
   */
  children: PropTypes.node,
  /**
   * Classes Dependencies
   */
  classes: classesPropType(['no-scroll']),
  /**
   * Enable/Disable accessibility features
   */
  enabled: PropTypes.bool,
  /**
   * Element to set as the initial focus
   */
  initialFocusRef: PropTypes.shape({
    current: PropTypes.shape({
      focus: PropTypes.func
    })
  }),
  /**
   * Callback to handle all events thaat trigger a modal close
   */
  onClose: PropTypes.func,
  /**
   * Callback to handle when clicking outside of the modal
   */
  onClickOutside: PropTypes.func,
  /**
   * Callback to handle when escape key is pressed
   */
  onEscapeKey: PropTypes.func,
  /**
   * Enable auto-focusing on the first focusable element. If `initialFocusRef` is given, this prop is ignored.
   */
  autoFocus: PropTypes.bool,
  /**
   * Passthrough props for the inner react-focus-lock FocusLock element.
   */
  focusLockProps: PropTypes.shape()
} : void 0;
ModalContainer.displayName = 'ModalContainer';
export default ModalContainer;